import * as React from "react";
import {
  IDropdownOption,
  Stack,
  DefaultButton,
  PrimaryButton,
  DatePicker,
  DayOfWeek,
  Dropdown,
  Checkbox,
  Breadcrumb,
  Callout,
  DirectionalHint,
} from "office-ui-fabric-react";
import {
  IDataCenter,
  ISearchDgSet,
  IDGSetReading,
  IReadingStatus,
} from "../../../vm";
import { useParams, useHistory } from "react-router-dom";
import { ToastContext } from "../../common/ToastProvider";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";
import {
  getADataCenter,
  getPowerComponents,
} from "../../../services/datacenter/DatacenterService";
import {
  getDgSetReadings,
  updateDgSetReadingStatus,
} from "../../../services/infrastructure/DgSetService";
import moment from "moment";
import NoDataTile from "../../common/NoDataTile";
import { Loading } from "../../../services/Loading";
import {
  childrenGap,
  UPS_STATUS,
  READ_AT_TIME_FORMAT,
  childrenGap8,
} from "../../common/Constants";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight, mdiDotsVertical } from "@mdi/js";
import ManageDGSetDialog from "./ManageDGSetDialog";
import {
  getThreshold,
  isInThreshold,
  isValueLessThanThreshold,
} from "../../../services/ThresholdService";
import ManageAllRejectDialog from "../../common/ManageAllRejectDialog";
import ShowThresholdIcon from "../../common/ShowThresholdIcon";

export interface DgSetMainProps { }

const DgSetMain: React.FC<DgSetMainProps> = () => {
  //state
  const [state, setState] = React.useState({
    isLoading: false,
    isDatacenterFound: false,
    isDgLoaded: false,
    allDgDropdownList: [],
    datacenter: undefined,
    searchObj: { datacenterId: "", date: new Date(), dgId: "" },
    dgReading: [],
    isAllChecked: false,
    selectedItems: [],
    manageDgDialog: {
      isOpen: false,
      index: undefined,
      data: undefined,
    },
    threshold: undefined,
    manageRejectDialog: {
      isOpen: false,
      ids: [],
      remarks: "",
    },
    manageActionsCallout: {
      isOpen: false,
      element: undefined,
      data: undefined,
    },
  } as {
    isLoading: boolean;
    isDatacenterFound: boolean;
    isDgLoaded: boolean;
    allDgDropdownList: IDropdownOption[];
    datacenter?: IDataCenter;
    searchObj: ISearchDgSet;
    dgReading: IDGSetReading[];
    isAllChecked: boolean;
    selectedItems: string[];
    manageDgDialog: {
      isOpen: boolean;
      index?: number;
      data?: IDGSetReading;
    };
    threshold: any;
    manageRejectDialog: {
      isOpen: boolean;
      ids: string[];
      remarks?: string;
    };
    manageActionsCallout: {
      isOpen: boolean;
      element: any;
      data?: IDGSetReading;
    };
  });

  //hooks
  const { showToast } = React.useContext(ToastContext);
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);
  const params: any = useParams();
  const history = useHistory();

  const isPuneDc2 = ["5f0570c337b0eb553b960600"].includes(params.dataCenterId);
  const isLvsb = ["5db81204cf818f6abcffa08e"].includes(params.dataCenterId);
  const isCalDc1 = ["5db6dd36b27b36223779fd7c"].includes(params.dataCenterId);

  React.useEffect(() => {
    const asyncFunc = async () => {
      if (params.dataCenterId) {
        getDatacenter(params.dataCenterId);
      }
    };
    asyncFunc();
  }, []);

  const getDatacenter = async (datacenterId: string) => {
    setState({ ...state, isLoading: true });
    let result = await getADataCenter(datacenterId);
    if (result?.success) {
      setState({
        ...state,
        datacenter: result.data,
        isDatacenterFound: true,
        isLoading: false,
      });

      getThresholdData(result.data);
    } else {
      showToast(result?.message || "Error while getting datacenter", "error");
      setState({ ...state, isDatacenterFound: false, isLoading: false });
    }
  };

  const getThresholdData = async (datacenter: IDataCenter) => {
    setState({ ...state, isLoading: true });
    let result = await getThreshold("dg");
    if (result?.success) {
      const threshold = result.data.threshold;
      if (isCalDc1) {
        threshold.oilTemperature.min = 30;
        threshold.coolingWaterTemperature.min = 25;
        threshold.engineOilPressure.min = 50;
        threshold.engineOilPressure.max = 85;
        threshold.dieselLevelInTank.min = 750;
        threshold.dieselLevelInTank.max = 990;
      }
      getPowerComponentsList(datacenter, threshold);
    } else {
      showToast(result?.message || "Error while getting datacenter", "error");
      setState({ ...state, isDatacenterFound: false, isLoading: false });
    }
  };

  const getPowerComponentsList = async (
    datacenter: IDataCenter,
    threshold?: any
  ) => {
    setState({ ...state, isLoading: true });
    let result = await getPowerComponents({
      datacenterId: params.dataCenterId,
      types: ["dg-set-ht", "dg-set-lt"],
    });
    if (result?.success) {
      let options: IDropdownOption[] = [];
      result.data.forEach((ele) => {
        options.push({ key: ele._id, text: ele.name });
      });
      let obj = {
        ...state,
        isLoading: false,
        datacenter,
        isDatacenterFound: true,
        searchObj: {
          datacenterId: params.dataCenterId,
          date: new Date(),
          dgId: options.length > 0 ? (options[0].key as string) : "",
        },
        allDgDropdownList: options,
        threshold,
      };
      setState(obj);
      getDgSetReadingsData(obj);
    } else {
      showToast(result?.message || "Error while getting dg set list", "error");
      setState({ ...state, isLoading: false });
    }
  };

  const getDgSetReadingsData = async (paramState?: any) => {
    let localState = { ...state };
    if (paramState != null) {
      localState = { ...paramState };
    }

    setState({ ...localState, isLoading: true });
    let obj = { ...localState.searchObj };
    obj.date = moment(obj.date).format("YYYY-MM-DD");
    let result = await getDgSetReadings(obj);
    if (result?.success) {
      setState({
        ...localState,
        dgReading: result.data,
        isLoading: false,
        isDgLoaded: true,
      });
    } else {
      showToast(
        result?.message || "Error while getting dg set readings",
        "error"
      );
      setState({ ...state, isLoading: false, isDgLoaded: false });
    }
  };

  const onSearch = async () => {
    if (!state.searchObj.dgId) {
      showToast("Please select an dg set before searching", "error");
      return;
    }
    getDgSetReadingsData();
  };

  const handleAcceptOrReject = (id: string, isAccept: boolean) => {
    showConfirmDialog(
      "Are You Sure",
      `Do you want to ${isAccept ? "Accept" : "Reject"} the dg set reading`,
      async () => {
        setState({
          ...state,
          isLoading: true,
          manageActionsCallout: {
            isOpen: false,
            element: undefined,
            data: undefined,
          },
        });
        let obj: IReadingStatus = {
          ids: [id],
          status: isAccept ? "approved" : "rejected",
        };
        let result = await updateDgSetReadingStatus(obj);
        if (result?.success) {
          let dgReading = [...state.dgReading];
          let index = dgReading.findIndex((ele) => ele._id === id);
          if (index !== -1) {
            dgReading[index].status = isAccept ? "approved" : "rejected";
          }
          let selectedItems = [...state.selectedItems];
          let indexToRemove = selectedItems.findIndex((ele) => ele === id);
          if (index !== -1) {
            selectedItems.splice(indexToRemove, 1);
          }
          showToast(
            `Dg set reading ${isAccept ? "approved" : "rejected"} successfully`,
            "success"
          );
          setState({
            ...state,
            dgReading,
            selectedItems,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              element: undefined,
              data: undefined,
            },
          });
        } else {
          showToast(
            result?.message ||
            `An error occurred while ${isAccept ? "approving" : "rejecting"
            } a dg set reading`,
            "error"
          );
          setState({
            ...state,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              element: undefined,
              data: undefined,
            },
          });
        }
      }
    );
  };

  const handleAcceptOrRejectSelected = (isAccept: boolean) => {
    if (isAccept) {
      showConfirmDialog(
        "Are You Sure",
        `Do you want to ${isAccept ? "Accept" : "Reject"
        } selected dg set reading`,
        async () => {
          let selectedItems = [...state.selectedItems];
          let dgReading = [...state.dgReading];
          setState({
            ...state,
            isLoading: true,
          });
          let obj: IReadingStatus = {
            ids: selectedItems,
            status: isAccept ? "approved" : "rejected",
          };
          let result = await updateDgSetReadingStatus(obj);
          if (result?.success) {
            selectedItems.forEach((item) => {
              let index = dgReading.findIndex((ele) => ele._id === item);
              if (index !== -1) {
                dgReading[index].status = isAccept ? "approved" : "rejected";
              }
            });
            showToast(
              `Dg set reading ${isAccept ? "approved" : "rejected"
              } successfully`,
              "success"
            );
            setState({
              ...state,
              dgReading,
              selectedItems: [],
              isLoading: false,
            });
          } else {
            showToast(
              result?.message ||
              `An error occurred while ${isAccept ? "approving" : "rejecting"
              } a dg set reading`,
              "error"
            );
            setState({
              ...state,
              isLoading: false,
            });
          }
        }
      );
    } else {
      let selectedItems = [...state.selectedItems];
      handleRejectAllOpenDialog(selectedItems);
    }
  };

  const onSelectAll = (isAllChecked: boolean) => {
    let selectedItems: string[] = [];
    if (isAllChecked) {
      state.dgReading.forEach((ele) => {
        if (ele.status === "submitted") {
          selectedItems.push(ele._id as string);
        }
      });
      setState({ ...state, isAllChecked: true, selectedItems });
    } else {
      setState({ ...state, isAllChecked: false, selectedItems });
    }
  };

  const onIndividualSelect = (id: string) => {
    let selectedItems = [...state.selectedItems];
    let index = selectedItems.findIndex((e) => e === id);
    if (index === -1) {
      selectedItems.push(id);
    } else {
      selectedItems.splice(index, 1);
    }
    let isAllChecked = true;
    state.dgReading.forEach((ele) => {
      if (
        ele.status === "submitted" &&
        selectedItems.includes(ele._id as string) === false
      ) {
        isAllChecked = false;
      }
    });
    setState({ ...state, selectedItems, isAllChecked });
  };

  const getMinValue = (property: string) => {
    let a = property.split(".");
    if (state.dgReading.length > 0) {
      let list = state.dgReading.map((ele: any) => {
        if (a[1]) {
          return ele[a[0]][a[1]];
        } else {
          return ele[a[0]];
        }
      });
      return Math.min(...list);
    } else {
      return "";
    }
  };
  const getMaxValue = (property: string) => {
    let a = property.split(".");
    if (state.dgReading.length > 0) {
      let list = state.dgReading.map((ele: any) => {
        if (a[1]) {
          return ele[a[0]][a[1]];
        } else {
          return ele[a[0]];
        }
      });
      return Math.max(...list);
    } else {
      return "";
    }
  };

  const onNextOrPrevFunc = (isPrev: boolean) => {
    let localState = { ...state };

    let currentSelecteddg = localState.searchObj.dgId;
    let currentDgList = localState.allDgDropdownList;

    let currentIndex = currentDgList.findIndex(
      (d) => d.key === currentSelecteddg
    );

    if (isPrev) {
      if (currentIndex !== 0) {
        localState = {
          ...localState,
          searchObj: {
            ...localState.searchObj,
            dgId: currentDgList[currentIndex - 1].key as string,
          },
        };
      }
    } else {
      if (currentIndex !== currentDgList.length - 1) {
        localState = {
          ...localState,
          searchObj: {
            ...localState.searchObj,
            dgId: currentDgList[currentIndex + 1].key as string,
          },
        };
      }
    }
    getDgSetReadingsData(localState);
  };

  const handleDialogOpen = (isOpen: boolean, id?: string) => {
    let dgReading = undefined;
    let index = undefined;
    if (id !== undefined) {
      index = state.dgReading.findIndex((ele) => ele._id === id);
      dgReading = { ...state.dgReading[index] };
    }
    setState({
      ...state,
      manageDgDialog: {
        isOpen: isOpen,
        index: index,
        data: dgReading,
      },
    });
  };

  const handleDialogClose = (data: IDGSetReading) => {
    let dgReading = [...state.dgReading];
    if (data) {
      if (state.manageDgDialog.index !== undefined) {
        dgReading[state.manageDgDialog.index] = data;
      }
    }
    setState({
      ...state,
      manageDgDialog: {
        isOpen: false,
        index: undefined,
        data: undefined,
      },
      dgReading,
    });
  };

  const handleRejectAllOpenDialog = (ids: string[]) => {
    setState({
      ...state,
      manageRejectDialog: {
        isOpen: true,
        ids: ids || [],
        remarks: "",
      },
    });
  };
  const handleRejectOpenDialog = (id: string, remarks?: string) => {
    setState({
      ...state,
      manageRejectDialog: {
        isOpen: true,
        ids: [id],
        remarks: remarks || "",
      },
    });
  };

  const handleRejectCloseDialog = async (
    isSuccess: boolean,
    remarks?: string
  ) => {
    let dgReading = [...state.dgReading];
    let selectedItems: string[] = state.manageRejectDialog.ids;
    if (isSuccess) {
      setState({
        ...state,
        isLoading: true,
      });
      let obj: IReadingStatus = {
        ids: selectedItems,
        status: "rejected",
        remarks: remarks || "",
      };
      let result = await updateDgSetReadingStatus(obj);
      if (result?.success) {
        selectedItems.forEach((item) => {
          let index = dgReading.findIndex((ele) => ele._id === item);
          if (index !== -1) {
            dgReading[index].status = "rejected";
            dgReading[index].remarks = remarks || "";
          }
        });
        showToast(`Dg set Reading rejected successfully`, "success");
        setState({
          ...state,
          dgReading,
          selectedItems: [],
          manageRejectDialog: {
            isOpen: false,
            ids: [],
            remarks: undefined,
          },
          isLoading: false,
        });
      } else {
        showToast(
          result?.message ||
          `An error occurred while rejecting a dg set reading`,
          "error"
        );
        setState({
          ...state,
          manageRejectDialog: {
            isOpen: false,
            ids: [],
            remarks: undefined,
          },
          isLoading: false,
        });
      }
    } else {
      setState({
        ...state,
        manageRejectDialog: {
          isOpen: false,
          ids: [],
          remarks: undefined,
        },
        dgReading,
      });
    }
  };

  const handleLoading = () => {
    setState({
      ...state,
      isLoading: !state.isLoading,
    });
  };

  const handleCallOutOpenClose = (
    isOpen: boolean,
    ele?: React.MouseEvent<HTMLSpanElement>,
    data?: IDGSetReading
  ) => {
    setState({
      ...state,
      manageActionsCallout: {
        isOpen,
        element: isOpen ? ele?.currentTarget : undefined,
        data,
      },
    });
  };

  return (
    <React.Fragment>
      {state.isLoading && <Loading />}
      {state.isDatacenterFound ? (
        <React.Fragment>
          <Stack>
            <Stack.Item>
              <Stack.Item>
                {/* <Text variant="xLarge">
                  DG Set Readings - {state.datacenter && state.datacenter.name}{" "}
                  {state.datacenter && state.datacenter.location && " - "}
                  {state.datacenter && state.datacenter.location}
                </Text> */}
                <Breadcrumb
                  items={[
                    {
                      text: state.datacenter
                        ? state.datacenter.name
                        : "Datacenter",
                      key: "datacenter",
                    },
                    {
                      text: "Infrastructures",
                      key: "infrastructure",
                      onClick: () => {
                        history.goBack();
                      },
                    },
                    {
                      text: "DG Set Readings",
                      key: "dg-set-readings",
                      isCurrentItem: true,
                    },
                  ]}
                />
              </Stack.Item>
            </Stack.Item>
          </Stack>
          <br />
          <Stack horizontal horizontalAlign="start">
            <Stack.Item>
              <Stack horizontal wrap tokens={childrenGap}>
                <Stack.Item>
                  <Dropdown
                    placeholder="Select a Dg Set"
                    // label="Role"
                    selectedKey={state.searchObj.dgId}
                    styles={{ dropdown: { width: 200 } }}
                    options={state.allDgDropdownList}
                    onChange={(e, v: IDropdownOption | undefined) => {
                      if (v) {
                        setState({
                          ...state,
                          searchObj: {
                            ...state.searchObj,
                            dgId: v.key as string,
                          },
                        });
                      }
                    }}
                  />
                </Stack.Item>
                <Stack.Item>
                  <DatePicker
                    firstDayOfWeek={DayOfWeek.Sunday}
                    showMonthPickerAsOverlay={true}
                    value={new Date(state.searchObj.date)}
                    showGoToToday={false}
                    onSelectDate={(date: Date | null | undefined) =>
                      setState({
                        ...state,
                        searchObj: { ...state.searchObj, date: date as Date },
                      })
                    }
                    placeholder="Select a date..."
                    ariaLabel="Select a date"
                    styles={{ root: { width: 200 } }}
                  />
                </Stack.Item>
                <Stack.Item>
                  <PrimaryButton text="Search" onClick={() => onSearch()} />
                </Stack.Item>
              </Stack>
            </Stack.Item>
            {/* <Stack.Item>
              <DefaultButton
                text="Accept"
                disabled={state.selectedItems.length === 0}
                onClick={() => handleAcceptOrRejectSelected(true)}
              />
              &nbsp;&nbsp;
              <DefaultButton
                text="Reject"
                disabled={state.selectedItems.length === 0}
                onClick={() => handleAcceptOrRejectSelected(false)}
              />
            </Stack.Item> */}
          </Stack>
          <br />
          <Stack horizontal horizontalAlign="space-between" wrap>
            <Stack.Item className="ml-6">
              <span
                onClick={
                  state.allDgDropdownList.length < 1 ||
                    state.allDgDropdownList[0]?.key === state.searchObj.dgId
                    ? undefined
                    : () => onNextOrPrevFunc(true)
                }
                className={
                  state.allDgDropdownList.length < 1 ||
                    state.allDgDropdownList[0]?.key === state.searchObj.dgId
                    ? "btn-disabled"
                    : "btn-enabled pointer"
                }
              >
                <Icon
                  path={mdiChevronLeft}
                  size={0.9}
                  className="btnIcon"
                  style={{
                    marginBottom: -6,
                    marginRight: -8,
                    color: "#135c8a",
                  }}
                />
                <DefaultButton
                  text="Previous DG"
                  className="borderless-btn"
                // disabled={
                //     state.allDgDropdownList[0].key ===
                //     state.searchObj.dgId
                //   }
                // onClick={() => {
                //   onNextOrPrevFunc(true);
                // }}
                />
              </span>
              <span className="nxt-prv-btn-border"></span>
              <span
                onClick={
                  state.allDgDropdownList.length < 1 ||
                    state.allDgDropdownList[state.allDgDropdownList.length - 1]
                      ?.key === state.searchObj.dgId
                    ? undefined
                    : () => onNextOrPrevFunc(false)
                }
                className={
                  state.allDgDropdownList.length < 1 ||
                    state.allDgDropdownList[state.allDgDropdownList.length - 1]
                      ?.key === state.searchObj.dgId
                    ? "btn-disabled"
                    : "btn-enabled pointer"
                }
              >
                <DefaultButton
                  text="Next DG"
                  // disabled={
                  //   state.allDgDropdownList[
                  //     state.allDgDropdownList.length - 1
                  //   ].key === state.searchObj.dgId
                  // }
                  // onClick={() => {
                  //   onNextOrPrevFunc(false);
                  // }}
                  className="borderless-btn"
                />
                <Icon
                  path={mdiChevronRight}
                  size={0.9}
                  className="btnIcon"
                  style={{ marginBottom: -6, marginLeft: -8, color: "#135c8a" }}
                />
              </span>
            </Stack.Item>
            <Stack.Item>
              <DefaultButton
                text="Accept"
                className="mr-8 custom-secondary-btn"
                disabled={state.selectedItems.length === 0}
                onClick={() => handleAcceptOrRejectSelected(true)}
              />
              <DefaultButton
                text="Reject"
                disabled={state.selectedItems.length === 0}
                onClick={() => handleAcceptOrRejectSelected(false)}
                className="custom-secondary-btn"
              />
            </Stack.Item>
          </Stack>
          {/* <Stack horizontal horizontalAlign="end" wrap>
            <Stack.Item>
              <DefaultButton
                text="Accept"
                className="mr-8"
                disabled={state.selectedItems.length === 0}
                onClick={() => handleAcceptOrRejectSelected(true)}
              />
            </Stack.Item>
            <Stack.Item>
              <DefaultButton
                text="Reject"
                disabled={state.selectedItems.length === 0}
                onClick={() => handleAcceptOrRejectSelected(false)}
              />
            </Stack.Item>
          </Stack> */}
          <br />
          {state.isDgLoaded && (
            <Stack>
              <div className="office-table bg-white">
                <table className="ms-Table bordered-table w-100">
                  <thead className="html-table-thead">
                    <tr>
                      <th rowSpan={2}>
                        <Checkbox
                          checked={state.isAllChecked}
                          onChange={(eve, checked) =>
                            onSelectAll(checked as boolean)
                          }
                        />
                      </th>
                      {/* <th rowSpan={2}>System</th> */}
                      <th rowSpan={2}>Oil Level</th>
                      <th rowSpan={2}>Water Level in Coolant Tank</th>
                      <th rowSpan={2}>Diesel Capacity in Tank (Litre)</th>
                      <th rowSpan={2}>Water Level in Battery Below Vent</th>
                      <th rowSpan={2}>Coolent Level</th>
                      <th rowSpan={2}>Battery Voltage</th>
                      <th rowSpan={2}>Cooling Tower Pipe Line Valves</th>
                      <th rowSpan={2}>Leakage In Hoses</th>
                      <th rowSpan={2}>
                        Active Alarm In Control Panel (If Any)
                      </th>
                      <th rowSpan={2}>D.G Set for Cleaness</th>
                      <th rowSpan={2}>Exhaust & Cooling Water Bellow</th>
                      <th rowSpan={2}>Inspect The Mounting (AVM Pads)</th>
                      {/* <th rowSpan={2}>DATE</th> */}
                      <th rowSpan={2}>Switched ON At</th>
                      <th rowSpan={2}>Switched OFF At</th>
                      {/* <th rowSpan={2}>Batt Volt</th>
                      <th rowSpan={2}>Cool Temp</th> */}
                      {/* <th rowSpan={2}>LOP</th>
                      <th rowSpan={2}>RPM</th>
                      <th rowSpan={2}>Fuel Temp</th>
                      <th rowSpan={2}>R.Hrs</th>
                      <th colSpan={3}>Volt</th>
                      <th colSpan={3}>Amps</th>
                      <th rowSpan={2}>KW</th>
                      <th rowSpan={2}>KVA</th>
                      <th rowSpan={2}>KWH</th>
                      <th rowSpan={2}>Hz</th>
                      <th rowSpan={2}>PF</th>
                      <th colSpan={2}>Main Radiator Temp</th>
                      <th colSpan={2}>LTA</th>
                      <th colSpan={2}>HSD</th>
                      <th colSpan={1}>PER Day CONS</th>
                      <th rowSpan={2}>Shift Tech Sign</th> */}
                      <th rowSpan={2}>{`Engine Oil Pressure ${isCalDc1 ? "(PSI)" : "(KPA)"
                        }`}</th>
                      <th rowSpan={2}>Oil Temp (℃)</th>
                      <th rowSpan={2}>
                        {!isPuneDc2
                          ? "Cooling Water Temp (℃)"
                          : "Coolant Temperature (℃)"}
                      </th>
                      <th rowSpan={2}>
                        {!isLvsb
                          ? "Battery Charging Voltage (Volt)"
                          : "Battery Current Parameter"}
                      </th>
                      <th rowSpan={2}>Engine R.P.M. Trip 1650</th>
                      <th rowSpan={2}>Exhaust Fans Function</th>
                      <th colSpan={3}>Hour Meter Reading</th>
                      <th colSpan={3}>K.W.H Meter Reading</th>
                      <th colSpan={3}>AMF Panel KWH Meter Reading</th>
                      <th colSpan={4}>Diesel Level (Day tank)</th>
                      <th rowSpan={2}>Average Unit/Litre</th>
                      <th colSpan={3}>Single Phase Voltage</th>
                      <th colSpan={3}>Three Phase Voltage</th>
                      <th rowSpan={2}>Load In AMPS Per Phase</th>
                      <th rowSpan={2}>KVA</th>
                      <th rowSpan={2}>KW</th>
                      <th rowSpan={2}>PF</th>
                      <th rowSpan={2}>Shift Tech Sign</th>
                      <th rowSpan={2}>Status</th>
                      <th rowSpan={2}>Remarks</th>
                      <th rowSpan={2}>Read At</th>
                      <th rowSpan={2}>Actions</th>
                    </tr>
                    <tr>
                      <th>Before Start</th>
                      <th>After Stop</th>
                      <th>Difference</th>
                      <th>Before Start</th>
                      <th>After Stop</th>
                      <th>Difference</th>
                      <th>Before Start</th>
                      <th>After Stop</th>
                      <th>Difference</th>
                      <th>Top Up To Day Tank</th>
                      <th>Before Start</th>
                      <th>After Stop</th>
                      <th>Difference</th>
                      <th>RN</th>
                      <th>YN</th>
                      <th>BN</th>
                      <th>RY</th>
                      <th>YB</th>
                      <th>BR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.dgReading.length === 0 ? (
                      <tr>
                        <td colSpan={50} align="center">
                          <br />
                          <i style={{ color: "#135c8a" }}>
                            No DG sets available
                          </i>
                          <br />
                          <br />
                        </td>
                      </tr>
                    ) : (
                      state.dgReading.map((item, index) => (
                        <tr key={index}>
                          <th>
                            <Checkbox
                              disabled={!item || item.status !== "submitted"}
                              checked={state.selectedItems.includes(
                                item?._id as string
                              )}
                              onChange={() =>
                                onIndividualSelect(item?._id as string)
                              }
                              className="ml-12"
                            />
                          </th>
                          {/* <td>
                            {item?.isSystemOn !== undefined
                              ? item?.isSystemOn === true
                                ? "On"
                                : "Off"
                              : ""}
                          </td> */}
                          {/* <th>
                            {item?.date
                              ? moment(item?.date).format("DD-MM-YYYY")
                              : ""}
                          </th> */}
                          {/* <td></td>
                          <td>
                            {item?.onTime
                              ? moment(item?.onTime).format("HH:mm DD-MM-YYYY")
                              : ""}
                          </td>
                          <td>
                            {item?.offTime
                              ? moment(item?.offTime).format("HH:mm DD-MM-YYYY")
                              : ""}
                          </td> */}
                          <td
                            className={
                              isInThreshold(
                                item?.oilLevel || "",
                                "oilLevel",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.oilLevel || ""}
                            {isInThreshold(
                              item?.oilLevel || "",
                              "oilLevel",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.oilLevel || "",
                                      "oilLevel",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.waterLevelInCoolingTank || "",
                                "waterLevelInCoolingTank",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.waterLevelInCoolingTank || ""}
                            {isInThreshold(
                              item?.waterLevelInCoolingTank || "",
                              "waterLevelInCoolingTank",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.waterLevelInCoolingTank || "",
                                      "waterLevelInCoolingTank",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.dieselLevelInTank || "",
                                "dieselLevelInTank",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.dieselLevelInTank || ""}
                            {isInThreshold(
                              item?.dieselLevelInTank || "",
                              "dieselLevelInTank",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.dieselLevelInTank || "",
                                      "dieselLevelInTank",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td>
                            {item?.isWaterLevelInBatteryBelowVent !== undefined
                              ? item.isWaterLevelInBatteryBelowVent === true
                                ? "Yes"
                                : "No"
                              : ""}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.coolentLevel || "",
                                "coolentLevel",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.coolentLevel || ""}
                            {isInThreshold(
                              item?.coolentLevel || "",
                              "coolentLevel",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.coolentLevel || "",
                                      "coolentLevel",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.batteryVoltage || "",
                                "batteryVoltage",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.batteryVoltage || ""}
                            {isInThreshold(
                              item?.batteryVoltage || "",
                              "batteryVoltage",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.batteryVoltage || "",
                                      "batteryVoltage",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td>
                            {item?.isCoolingTowerPipeLineValvesOpen !==
                              undefined
                              ? item.isCoolingTowerPipeLineValvesOpen === true
                                ? "Open"
                                : "Close"
                              : ""}
                          </td>
                          <td>
                            {item?.isLeakageInHoses !== undefined
                              ? item.isLeakageInHoses === true
                                ? "Leakages"
                                : "No Leakages"
                              : ""}
                          </td>
                          {/* <td>{item?.volt || ""}</td>
                          <td>{item?.amps.R || ""}</td>
                          <td>{item?.amps.Y || ""}</td>
                          <td>{item?.amps.B || ""}</td>
                          <td>{item?.kw || ""}</td>
                          <td>{item?.kva || ""}</td>
                          <td>{item?.kwh || ""}</td>
                          <td>{item?.hz || ""}</td>
                          <td>{item?.pf || ""}</td>
                          <td>{item?.mainRadiator.intel || ""}</td>
                          <td>{item?.mainRadiator.outlet || ""}</td>
                          <td>{item?.lta.inTemp || ""}</td>
                          <td>{item?.lta.outTemp || ""}</td>
                          <td>{item?.hsd.cons || ""}</td>
                          <td>{item?.hsd.level || ""}</td>
                          <td>{item?.perDayConsKWH || ""}</td> */}
                          <td>
                            {item?.anyAlarmInControlPanel !== undefined
                              ? item.anyAlarmInControlPanel === true
                                ? "Alarms"
                                : "No Alarms"
                              : ""}
                          </td>
                          <td>
                            {item?.isDGSetClean !== undefined
                              ? item.isDGSetClean === true
                                ? "Clean"
                                : "Not Clean"
                              : ""}
                          </td>
                          <td>
                            {item?.exhaustAndCoolingWaterBellow !== undefined
                              ? item.exhaustAndCoolingWaterBellow === true
                                ? "Yes"
                                : "No"
                              : ""}
                          </td>
                          <td>
                            {item?.AnyDamageInMountingAVMPad !== undefined
                              ? item.AnyDamageInMountingAVMPad === true
                                ? "Damages"
                                : "No Damages"
                              : ""}
                          </td>
                          <td>
                            {item?.onTime
                              ? moment(item.onTime).format("HH:mm DD-MM-YYYY")
                              : ""}
                          </td>
                          <td>
                            {item?.offTime
                              ? moment(item.offTime).format("HH:mm DD-MM-YYYY")
                              : ""}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.engineOilPressure || "",
                                "engineOilPressure",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.engineOilPressure || ""}
                            {isInThreshold(
                              item?.engineOilPressure || "",
                              "engineOilPressure",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.engineOilPressure || "",
                                      "engineOilPressure",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.oilTemperature || "",
                                "oilTemperature",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.oilTemperature || ""}
                            {isInThreshold(
                              item?.oilTemperature || "",
                              "oilTemperature",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.oilTemperature || "",
                                      "oilTemperature",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.coolingWaterTemperature || "",
                                "coolingWaterTemperature",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.coolingWaterTemperature || ""}
                            {isInThreshold(
                              item?.coolingWaterTemperature || "",
                              "coolingWaterTemperature",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.coolingWaterTemperature || "",
                                      "coolingWaterTemperature",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.batteryChargingVoltage || "",
                                "batteryChargingVoltage",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.batteryChargingVoltage || ""}
                            {isInThreshold(
                              item?.batteryChargingVoltage || "",
                              "batteryChargingVoltage",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.batteryChargingVoltage || "",
                                      "batteryChargingVoltage",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.engineRPMTrip1650 || "",
                                "engineRPMTrip1650",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.engineRPMTrip1650 || ""}
                            {isInThreshold(
                              item?.engineRPMTrip1650 || "",
                              "engineRPMTrip1650",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.engineRPMTrip1650 || "",
                                      "engineRPMTrip1650",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td>
                            {item?.exhaustFansFunction !== undefined
                              ? item.exhaustFansFunction === true
                                ? "On"
                                : "Off"
                              : ""}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.hourMeterReading?.beforeStart || "",
                                "hourMeterReading.beforeStart",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.hourMeterReading?.beforeStart || ""}
                            {isInThreshold(
                              item?.hourMeterReading?.beforeStart || "",
                              "hourMeterReading.beforeStart",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.hourMeterReading?.beforeStart || "",
                                      "hourMeterReading.beforeStart",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.hourMeterReading?.afterStop || "",
                                "hourMeterReading.afterStop",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.hourMeterReading?.afterStop || ""}
                            {isInThreshold(
                              item?.hourMeterReading?.afterStop || "",
                              "hourMeterReading.afterStop",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.hourMeterReading?.afterStop || "",
                                      "hourMeterReading.afterStop",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.hourMeterReading?.diff || "",
                                "hourMeterReading.diff",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.hourMeterReading?.diff || ""}
                            {isInThreshold(
                              item?.hourMeterReading?.diff || "",
                              "hourMeterReading.diff",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.hourMeterReading?.diff || "",
                                      "hourMeterReading.diff",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.kwhMeterReading?.beforeStart || "",
                                "kwhMeterReading.beforeStart",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.kwhMeterReading?.beforeStart || ""}
                            {isInThreshold(
                              item?.kwhMeterReading?.beforeStart || "",
                              "kwhMeterReading.beforeStart",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.kwhMeterReading?.beforeStart || "",
                                      "kwhMeterReading.beforeStart",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.kwhMeterReading?.afterStop || "",
                                "kwhMeterReading.afterStop",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.kwhMeterReading?.afterStop || ""}
                            {isInThreshold(
                              item?.kwhMeterReading?.afterStop || "",
                              "kwhMeterReading.afterStop",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.kwhMeterReading?.afterStop || "",
                                      "kwhMeterReading.afterStop",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.kwhMeterReading?.diff || "",
                                "kwhMeterReading.diff",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.kwhMeterReading?.diff || ""}
                            {isInThreshold(
                              item?.kwhMeterReading?.diff || "",
                              "kwhMeterReading.diff",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.kwhMeterReading?.diff || "",
                                      "kwhMeterReading.diff",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.amfPanelKWHMeterReading?.beforeStart ||
                                "",
                                "amfPanelKWHMeterReading.beforeStart",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.amfPanelKWHMeterReading?.beforeStart || ""}
                            {isInThreshold(
                              item?.amfPanelKWHMeterReading?.beforeStart || "",
                              "amfPanelKWHMeterReading.beforeStart",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.amfPanelKWHMeterReading
                                        ?.beforeStart || "",
                                      "amfPanelKWHMeterReading.beforeStart",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.amfPanelKWHMeterReading?.afterStop || "",
                                "amfPanelKWHMeterReading.afterStop",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.amfPanelKWHMeterReading?.afterStop || ""}
                            {isInThreshold(
                              item?.amfPanelKWHMeterReading?.afterStop || "",
                              "amfPanelKWHMeterReading.afterStop",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.amfPanelKWHMeterReading?.afterStop ||
                                      "",
                                      "amfPanelKWHMeterReading.afterStop",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.amfPanelKWHMeterReading?.diff || "",
                                "amfPanelKWHMeterReading.diff",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.amfPanelKWHMeterReading?.diff || ""}
                            {isInThreshold(
                              item?.amfPanelKWHMeterReading?.diff || "",
                              "amfPanelKWHMeterReading.diff",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.amfPanelKWHMeterReading?.diff || "",
                                      "amfPanelKWHMeterReading.diff",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.dieselLevelDayTank?.topUpToDayTank || "",
                                "dieselLevelDayTank.topUpToDayTank",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.dieselLevelDayTank?.topUpToDayTank || ""}
                            {isInThreshold(
                              item?.dieselLevelDayTank?.topUpToDayTank || "",
                              "dieselLevelDayTank.topUpToDayTank",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.dieselLevelDayTank?.topUpToDayTank ||
                                      "",
                                      "dieselLevelDayTank.topUpToDayTank",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.dieselLevelDayTank?.beforeStart || "",
                                "dieselLevelDayTank.beforeStart",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.dieselLevelDayTank?.beforeStart || ""}
                            {isInThreshold(
                              item?.dieselLevelDayTank?.beforeStart || "",
                              "dieselLevelDayTank.beforeStart",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.dieselLevelDayTank?.beforeStart || "",
                                      "dieselLevelDayTank.beforeStart",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.dieselLevelDayTank?.afterStop || "",
                                "dieselLevelDayTank.afterStop",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.dieselLevelDayTank?.afterStop || ""}
                            {isInThreshold(
                              item?.dieselLevelDayTank?.afterStop || "",
                              "dieselLevelDayTank.afterStop",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.dieselLevelDayTank?.afterStop || "",
                                      "dieselLevelDayTank.afterStop",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.dieselLevelDayTank?.diff || "",
                                "dieselLevelDayTank.diff",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.dieselLevelDayTank?.diff || ""}
                            {isInThreshold(
                              item?.dieselLevelDayTank?.diff || "",
                              "dieselLevelDayTank.diff",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.dieselLevelDayTank?.diff || "",
                                      "dieselLevelDayTank.diff",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.averageUnit || "",
                                "averageUnit",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.averageUnit || ""}
                            {isInThreshold(
                              item?.averageUnit || "",
                              "averageUnit",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.averageUnit || "",
                                      "averageUnit",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.singlePhaseVoltage?.RN || "",
                                "singlePhaseVoltage.RN",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.singlePhaseVoltage?.RN || ""}
                            {isInThreshold(
                              item?.singlePhaseVoltage?.RN || "",
                              "singlePhaseVoltage.RN",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.singlePhaseVoltage?.RN || "",
                                      "singlePhaseVoltage.RN",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.singlePhaseVoltage?.YN || "",
                                "singlePhaseVoltage.YN",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.singlePhaseVoltage?.YN || ""}
                            {isInThreshold(
                              item?.singlePhaseVoltage?.YN || "",
                              "singlePhaseVoltage.YN",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.singlePhaseVoltage?.YN || "",
                                      "singlePhaseVoltage.YN",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.singlePhaseVoltage?.BN || "",
                                "singlePhaseVoltage.BN",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.singlePhaseVoltage?.BN || ""}
                            {isInThreshold(
                              item?.singlePhaseVoltage?.BN || "",
                              "singlePhaseVoltage.BN",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.singlePhaseVoltage?.BN || "",
                                      "singlePhaseVoltage.BN",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.threePhaseVoltage?.RY || "",
                                "threePhaseVoltage.RY",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.threePhaseVoltage?.RY || ""}
                            {isInThreshold(
                              item?.threePhaseVoltage?.RY || "",
                              "threePhaseVoltage.RY",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.threePhaseVoltage?.RY || "",
                                      "threePhaseVoltage.RY",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.threePhaseVoltage?.YB || "",
                                "threePhaseVoltage.YB",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.threePhaseVoltage?.YB || ""}
                            {isInThreshold(
                              item?.threePhaseVoltage?.YB || "",
                              "threePhaseVoltage.YB",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.threePhaseVoltage?.YB || "",
                                      "threePhaseVoltage.YB",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.threePhaseVoltage?.BR || "",
                                "threePhaseVoltage.BR",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.threePhaseVoltage?.BR || ""}
                            {isInThreshold(
                              item?.threePhaseVoltage?.BR || "",
                              "threePhaseVoltage.BR",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.threePhaseVoltage?.BR || "",
                                      "threePhaseVoltage.BR",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.loadInAmpsPerPhase || "",
                                "loadInAmpsPerPhase",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.loadInAmpsPerPhase || ""}
                            {isInThreshold(
                              item?.loadInAmpsPerPhase || "",
                              "loadInAmpsPerPhase",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.loadInAmpsPerPhase || "",
                                      "loadInAmpsPerPhase",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.kva || "",
                                "kva",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.kva || ""}
                            {isInThreshold(
                              item?.kva || "",
                              "kva",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.kva || "",
                                      "kva",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.kw || "",
                                "kw",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.kw || ""}
                            {isInThreshold(
                              item?.kw || "",
                              "kw",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.kw || "",
                                      "kw",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>
                          <td
                            className={
                              isInThreshold(
                                item?.pf || "",
                                "pf",
                                state.threshold
                              )
                                ? "text-danger"
                                : ""
                            }
                          >
                            {item?.pf || ""}
                            {isInThreshold(
                              item?.pf || "",
                              "pf",
                              state.threshold
                            ) && (
                                <ShowThresholdIcon
                                  isLessThan={
                                    isValueLessThanThreshold(
                                      item?.pf || "",
                                      "pf",
                                      state.threshold
                                    ) as boolean
                                  }
                                />
                              )}
                          </td>

                          <td>{item?.recordedBy || ""}</td>
                          <td>
                            {item && item.status ? UPS_STATUS[item.status] : ""}
                          </td>
                          <td>{item?.remarks || ""}</td>
                          <td>
                            {item?.date
                              ? moment(item.date).format(READ_AT_TIME_FORMAT)
                              : ""}
                          </td>
                          <td align="center" className="no-wrap">
                            {item && item.status === "submitted" && (
                              <span
                                onClick={(ele) =>
                                  handleCallOutOpenClose(true, ele, item)
                                }
                              >
                                <Icon
                                  path={mdiDotsVertical}
                                  size={0.9}
                                  className="pointer"
                                  color="#135c8a"
                                />
                              </span>
                              // <React.Fragment>
                              //   <TooltipHost
                              //     content="Update"
                              //     calloutProps={{ gapSpace: 0 }}
                              //   >
                              //     <span
                              //       onClick={() =>
                              //         handleDialogOpen(
                              //           true,
                              //           item?._id as string
                              //         )
                              //       }
                              //     >
                              //       <Icon
                              //         path={mdiPencil}
                              //         size={0.7}
                              //         className="pointer"
                              //       />
                              //     </span>
                              //   </TooltipHost>
                              //   &nbsp;&nbsp;
                              //   <TooltipHost
                              //     content="Accept"
                              //     calloutProps={{ gapSpace: 0 }}
                              //   >
                              //     <span
                              //       onClick={() =>
                              //         handleAcceptOrReject(
                              //           item?._id as string,
                              //           true
                              //         )
                              //       }
                              //     >
                              //       <Icon
                              //         path={mdiCheck}
                              //         size={0.8}
                              //         className="pointer"
                              //         color="green"
                              //       />
                              //     </span>
                              //   </TooltipHost>
                              //   &nbsp;&nbsp;
                              //   <TooltipHost
                              //     content="Reject"
                              //     calloutProps={{ gapSpace: 0 }}
                              //   >
                              //     <span
                              //       onClick={() =>
                              //         handleRejectOpenDialog(
                              //           item?._id as string,
                              //           item?.remarks
                              //         )
                              //       }
                              //     >
                              //       <Icon
                              //         path={mdiClose}
                              //         size={0.8}
                              //         className="pointer"
                              //         color="red"
                              //       />
                              //     </span>
                              //   </TooltipHost>
                              // </React.Fragment>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* <div className="next-prev-btn">
                <div>
                  <DefaultButton
                    text="Previous DG"
                    className="mr-8"
                    disabled={
                      state.allDgDropdownList[0].key === state.searchObj.dgId
                    }
                    onClick={() => {
                      onNextOrPrevFunc(true);
                    }}
                  />
                  <DefaultButton
                    text="Next DG"
                    disabled={
                      state.allDgDropdownList[
                        state.allDgDropdownList.length - 1
                      ].key === state.searchObj.dgId
                    }
                    onClick={() => {
                      onNextOrPrevFunc(false);
                    }}
                  />
                </div>
              </div> */}
            </Stack>
          )}
        </React.Fragment>
      ) : (
        <NoDataTile
          title="No Datacenter Found"
          description="Datacenter either deleted or not created"
        />
      )}
      {state.manageActionsCallout.isOpen && (
        <Callout
          styles={{ root: { width: 95, padding: 20 } }}
          role="alertdialog"
          ariaLabelledBy={
            state.manageActionsCallout && state.manageActionsCallout.data?._id
          }
          gapSpace={0}
          target={state.manageActionsCallout.element}
          directionalHint={DirectionalHint.leftCenter}
          onDismiss={() => handleCallOutOpenClose(false)}
        // setInitialFocus={true}
        >
          <Stack tokens={childrenGap8}>
            <Stack.Item className="pointer">
              <span
                onClick={() =>
                  handleDialogOpen(
                    true,
                    state.manageActionsCallout.data?._id as string
                  )
                }
                className="callout-options"
              >
                Edit
              </span>
            </Stack.Item>
            <Stack.Item className="pointer">
              <span
                onClick={() =>
                  handleAcceptOrReject(
                    state.manageActionsCallout.data?._id as string,
                    true
                  )
                }
                className="callout-options"
              >
                Accept
              </span>
            </Stack.Item>
            <Stack.Item className="pointer">
              <span
                onClick={() =>
                  handleRejectOpenDialog(
                    state.manageActionsCallout.data?._id as string,
                    state.manageActionsCallout.data?.remarks
                  )
                }
                className="callout-options"
              >
                Reject
              </span>
            </Stack.Item>
          </Stack>
        </Callout>
      )}
      {state.manageDgDialog.isOpen && (
        <ManageDGSetDialog
          data={state.manageDgDialog.data}
          handleDialogClose={(data: IDGSetReading) => handleDialogClose(data)}
          handleLoading={handleLoading}
          threshold={state.threshold}
        />
      )}
      {state.manageRejectDialog.isOpen && (
        <ManageAllRejectDialog
          handleRejectCloseDialog={(isSuccess: boolean, remarks?: string) =>
            handleRejectCloseDialog(isSuccess, remarks)
          }
          remarks={state.manageRejectDialog.remarks}
          subText="dg set reading"
        />
      )}
    </React.Fragment>
  );
};

export default DgSetMain;
